import { ecrireJson, lireJson, type Resultat } from './client';
import type { Role } from './auth';

/** Un compte tel que l'écran d'administration le voit. */
export interface CompteAdmin {
  readonly id: string;
  readonly email: string;
  readonly nom: string;
  readonly role: Role;
  readonly roleLibelle: string;
  readonly actif: boolean;
  readonly creeLe: string;
  /** Date d'archivage : `null` tant que le compte est vivant. */
  readonly archiveLe: string | null;
  /** Décision du SERVEUR : l'archive a dépassé la rétention, elle est purgeable. */
  readonly echue?: boolean;
  /** VOLUME de clients du compte — jamais leur contenu. */
  readonly clients: number;
}

export interface ListeComptes {
  readonly items: readonly CompteAdmin[];
  readonly total: number;
}

/**
 * Ce qu'un PATCH de compte peut changer : le RÔLE, et rien d'autre.
 *
 * La suspension n'a qu'un chemin, l'archivage : deux portes pour un même mot
 * donnaient deux états que l'écran ne savait plus distinguer.
 */
export interface ChangementCompte {
  readonly role: Role;
}

/** Tous les comptes. Le serveur refuse (403) si le compte connecté n'est pas admin. */
export function listerComptes(
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ListeComptes>> {
  return lireJson<ListeComptes>('/api/admin/comptes', options);
}

/** Change le rôle d'un compte. */
export function modifierCompte(
  id: string,
  changement: ChangementCompte,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<CompteAdmin>> {
  return ecrireJson<CompteAdmin>(
    `/api/admin/comptes/${encodeURIComponent(id)}`,
    'PATCH',
    changement,
    options,
  );
}

/** Les comptes archivés, avec ce que la rétention autorise encore à purger. */
export interface ListeArchives {
  readonly items: readonly CompteAdmin[];
  readonly total: number;
  /** Durée de rétention annoncée par le SERVEUR : l'écran ne la devine pas. */
  readonly retentionJours: number;
  /** Nombre d'archives dont la rétention est dépassée — ce que la purge effacerait. */
  readonly echues: number;
  /** Instant limite, au format de la base : au-delà, une archive est échue. */
  readonly limite: string;
}

export function listerArchives(
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ListeArchives>> {
  return lireJson<ListeArchives>('/api/admin/comptes/archives', options);
}

/** Met un compte de côté : il quitte les listes ordinaires et se restaure. */
export function archiverCompte(
  id: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<CompteAdmin>> {
  return ecrireJson<CompteAdmin>(
    `/api/admin/comptes/${encodeURIComponent(id)}/archivage`,
    'POST',
    {},
    options,
  );
}

/** Rend un compte archivé à la vie courante (il redevient actif). */
export function restaurerCompte(
  id: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<CompteAdmin>> {
  return ecrireJson<CompteAdmin>(
    `/api/admin/comptes/${encodeURIComponent(id)}/archivage`,
    'DELETE',
    {},
    options,
  );
}

export interface ResultatPurge {
  readonly purges: number;
  readonly message?: string;
}

/**
 * Efface DÉFINITIVEMENT les archives échues.
 *
 * Le corps porte la confirmation : le serveur refuse une purge non confirmée,
 * parce qu'elle emporte le portefeuille des comptes concernés.
 */
export interface ResultatLot {
  readonly id: string;
  readonly email?: string | null;
  readonly ok: boolean;
  readonly message?: string;
}

/** Ce que le serveur a fait, identifiant par identifiant. */
export interface ReponseLot {
  readonly resultats: readonly ResultatLot[];
  readonly faits: number;
  readonly refuses: number;
}

/**
 * Archive PLUSIEURS comptes en un appel.
 *
 * La règle est appliquée par le serveur à chaque identifiant : un refus n'annule
 * pas les autres, et l'écran n'a aucune décision à prendre.
 */
export function archiverComptes(
  ids: readonly string[],
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ReponseLot>> {
  return ecrireJson<ReponseLot>('/api/admin/comptes/archivages', 'POST', { ids }, options);
}

export function purgerArchives(
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ResultatPurge>> {
  return ecrireJson<ResultatPurge>(
    '/api/admin/archives/purge',
    'POST',
    { confirmer: true },
    options,
  );
}
